/**
 * OpenTelemetry browser instrumentation — Frontend RUM (Real User Monitoring).
 *
 * Captura traces do browser e propaga `traceparent` pras chamadas à Rocky.WebAPI,
 * resultando em traces distribuídas: browser → API → DB tudo na mesma visualização
 * do SigNoz.
 *
 * Auto-instrumentação habilitada:
 *   - document-load: tempo de carregamento da página (DNS, TLS, TTFB, etc.)
 *   - fetch: cada chamada `fetch()` vira um span
 *   - xhr: cada `XMLHttpRequest` vira um span
 *   - error capture: window.onerror + unhandledrejection viram spans com status ERROR
 *
 * Nota de performance: NÃO usamos ZoneContextManager nem UserInteractionInstrumentation.
 * O zone.js faz patch de todo async (custo alto/jank no browser) e o span-por-clique gera
 * volume sem valor proporcional. O tracing distribuído (browser→API→DB) continua via fetch/xhr.
 *
 * User context injetado em CADA span (via SpanProcessor):
 *   - user.id, user.roles (lidos do JWT `rocky_token` no localStorage)
 *   - PII (email, nome) NÃO é enviado — LGPD
 *
 * Init no topo do `index.tsx` ANTES de qualquer outro import que faça I/O.
 * Se `VITE_OTEL_TRACES_ENDPOINT` não estiver setada, vira no-op (útil em dev/testes).
 */

import {
  context,
  trace,
  type Context,
  type Span,
  SpanStatusCode,
  type Tracer
} from '@opentelemetry/api';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  BatchSpanProcessor,
  type ReadableSpan,
  type SpanProcessor,
  WebTracerProvider
} from '@opentelemetry/sdk-trace-web';
import { jwtDecode } from 'jwt-decode';

const SERVICE_NAME = 'rocky-web';
// Mesma key usada em modules/auth/services/authService.ts (TOKEN_KEY).
const ACCESS_TOKEN_KEY = 'rocky_token';

// Semantic conventions strings — evita dep no subpath /incubating (precisa moduleResolution: bundler).
const ATTR_SERVICE_NAME = 'service.name';
const ATTR_SERVICE_VERSION = 'service.version';
const ATTR_DEPLOYMENT_ENVIRONMENT_NAME = 'deployment.environment.name';

export function initTelemetria(): void {
  const endpoint = import.meta.env.VITE_OTEL_TRACES_ENDPOINT as string | undefined;

  if (!endpoint) {
    console.info('[otel] VITE_OTEL_TRACES_ENDPOINT não definido — telemetria desabilitada');
    return;
  }

  const apiUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined) || window.location.origin;
  const version = (import.meta.env.VITE_APP_VERSION as string | undefined) || 'dev';
  const env = import.meta.env.PROD ? 'Production' : 'Development';

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: SERVICE_NAME,
    [ATTR_SERVICE_VERSION]: version,
    [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: env
  });

  const exporter = new OTLPTraceExporter({
    url: endpoint,
    concurrencyLimit: 10
  });

  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [
      // Anota user.id, user.roles em CADA span ANTES do batch ser empurrado.
      // Ordem importa — esse vem antes do BatchSpanProcessor.
      new UserContextSpanProcessor(),
      new BatchSpanProcessor(exporter, {
        maxQueueSize: 100,
        maxExportBatchSize: 20,
        scheduledDelayMillis: 5_000
      })
    ]
  });

  // Context manager padrão (StackContextManager). Antes usávamos ZoneContextManager,
  // que faz patch de TODO async via zone.js — custo alto no browser (jank em SPA) e
  // desnecessário: a instrumentação de fetch/xhr já cria os spans e propaga o traceparent.
  provider.register();

  const propagateUrls = [new RegExp(escapeRegExp(apiUrl))];
  // Não instrumentar o POST do próprio exporter pro SigNoz — senão cada export vira um span
  // que gera outro export (loop de ruído, pior se o coletor estiver lento).
  const ignoreUrls = [new RegExp(escapeRegExp(endpoint))];

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: propagateUrls,
        ignoreUrls,
        clearTimingResources: true
      }),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: propagateUrls,
        ignoreUrls,
        clearTimingResources: true
      })
      // UserInteractionInstrumentation (span por clique) removido: gerava volume alto de
      // spans e envolvia listeners via zone.js — custo client-side sem valor proporcional.
    ]
  });

  const tracer = trace.getTracer(SERVICE_NAME, version);

  // Captura de erros JS globais — window.onerror + unhandled promise rejection.
  setupErrorCapture(tracer);

  // Span manual marcando o boot — útil pra confirmar que init rodou.
  context.with(context.active(), () => {
    const span = tracer.startSpan('app.bootstrap');
    span.setAttribute('environment', env);
    span.end();
  });

  console.info(`[otel] inicializado · ${SERVICE_NAME}@${version} · env=${env} · ${endpoint}`);
}

// ──────────────────────────────────────────────────────────────────────
// User context — injeta atributos LGPD-safe em cada span
// ──────────────────────────────────────────────────────────────────────

/**
 * Claims do JWT emitido por Rocky.Modules.Auth.Services.TokenService:
 *   - nameid → User.Id (ClaimTypes.NameIdentifier serializa como "nameid" no JWT v1)
 *   - role   → role do user (ClaimTypes.Role)
 *
 * Os outros claims (email, nome, avatar) NÃO entram no span — PII.
 */
interface RockyJwt {
  nameid?: string;
  role?: string | string[];
}

// Cache do decode: onStart roda em CADA span (fetch/xhr/erro), então decodificar o JWT
// toda vez custa caro sob volume. Decodifica só quando o token muda.
let cachedToken: string | null = null;
let cachedAttrs: Record<string, string> | null = null;

function getUserContextAttrs(): Record<string, string> | null {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!token) {
    cachedToken = null;
    cachedAttrs = null;
    return null;
  }
  if (token === cachedToken) return cachedAttrs;

  cachedToken = token;
  try {
    const payload = jwtDecode<RockyJwt>(token);
    const attrs: Record<string, string> = {};

    if (payload.nameid) attrs['user.id'] = payload.nameid;

    const roles = Array.isArray(payload.role)
      ? payload.role
      : payload.role
        ? [payload.role]
        : [];
    if (roles.length > 0) attrs['user.roles'] = roles.join(',');

    cachedAttrs = Object.keys(attrs).length > 0 ? attrs : null;
  } catch {
    // JWT inválido/expirado/malformado — sem context, sem erro.
    cachedAttrs = null;
  }
  return cachedAttrs;
}

class UserContextSpanProcessor implements SpanProcessor {
  onStart(span: Span, _parentContext: Context): void {
    const attrs = getUserContextAttrs();
    if (!attrs) return;
    for (const [k, v] of Object.entries(attrs)) {
      span.setAttribute(k, v);
    }
  }

  // onEnd, shutdown, forceFlush são no-op — o trabalho é no onStart.
  onEnd(_span: ReadableSpan): void {
    /* no-op */
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }
}

// ──────────────────────────────────────────────────────────────────────
// Error capture — JS errors + unhandled promise rejections viram spans
// ──────────────────────────────────────────────────────────────────────

function setupErrorCapture(tracer: Tracer): void {
  // Erros sync (throw em handlers, scripts externos, etc).
  window.addEventListener('error', (event: ErrorEvent) => {
    const span = tracer.startSpan('window.error', {
      attributes: {
        'error.kind': 'window.error',
        'error.filename': event.filename || '',
        'error.lineno': event.lineno || 0,
        'error.colno': event.colno || 0,
        'page.url': window.location.href,
        'page.pathname': window.location.pathname
      }
    });
    if (event.error instanceof Error) {
      span.recordException(event.error);
    } else if (event.message) {
      span.setAttribute('exception.message', event.message);
    }
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: event.message || 'window error'
    });
    span.end();
  });

  // Promises não tratadas (await falhando sem catch, .then sem .catch).
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const span = tracer.startSpan('window.unhandledrejection', {
      attributes: {
        'error.kind': 'unhandled_promise_rejection',
        'page.url': window.location.href,
        'page.pathname': window.location.pathname
      }
    });
    const reason = event.reason;
    let message = 'unhandled rejection';
    if (reason instanceof Error) {
      span.recordException(reason);
      message = reason.message;
    } else {
      span.setAttribute('exception.message', String(reason));
      message = String(reason);
    }
    span.setStatus({ code: SpanStatusCode.ERROR, message });
    span.end();
  });
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
